import { useCallback, useEffect, useState } from 'react'

/**
 * Light or dark, and remembering which.
 *
 * The stylesheet has carried both palettes since the first screen; nothing ever
 * let anyone choose. Three states, not two: an explicit choice wins, and with
 * no choice made the operating system decides — which is why the attribute is
 * removed rather than set to a default. A product that ignores a system-wide
 * dark preference until you find its own switch has made a decision for
 * somebody who already made it.
 *
 * The stored value is read again in `index.html` before React runs. It has to
 * be: by the time this hook executes the browser has painted, and someone who
 * chose dark would see a white flash on every load.
 */

const KEY = 'carbonwebmail.theme'

export type Theme = 'light' | 'dark'

function systemPrefersDark(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true
}

function stored(): Theme | undefined {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'dark' || value === 'light' ? value : undefined
  } catch {
    // Private browsing, or storage disabled. Not an error: the system
    // preference is a perfectly good answer.
    return undefined
  }
}

export function useTheme(): { readonly theme: Theme; readonly toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(
    () => stored() ?? (systemPrefersDark() ? 'dark' : 'light'),
  )

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next = current === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem(KEY, next)
      } catch {
        // The choice still applies to this tab; it just will not survive a
        // reload. Better than refusing to switch at all.
      }
      return next
    })
  }, [])

  return { theme, toggle }
}
